/**
 * DayView - Single day view with Morning/Afternoon/Evening segments.
 *
 * Shows tasks as cards within time segments. Small tasks can appear side-by-side
 * (max 2 columns) for visual parallelism. Waiting tasks (assigned to others)
 * are shown in a separate group at the bottom.
 */

import React, { useMemo } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { Participant, PlanRole } from '../../../types/generated/graphql';
import {
  ScheduleResult,
  ScheduledStep,
  formatDayHeader,
  getTasksForDate,
} from '../../../utils/calendarScheduler';
import { colors } from '../../../theme/colors';
import TaskCard from './TaskCard';

interface DayViewProps {
  scheduleResult: ScheduleResult;
  currentDate: Date;
  onDateChange: (date: Date) => void;
  participants: Participant[];
  roles?: PlanRole[];
  currentParticipantId: string | null;
  onNavigateToTask: (taskId: string) => void;
}

/**
 * Add days to a date.
 */
function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Get assignee from participants.
 */
function getAssignee(
  step: ScheduledStep,
  participants: Participant[]
): Participant | null {
  if (!step.step.assignedToId) return null;
  return participants.find((p) => p.id === step.step.assignedToId) || null;
}

/**
 * Segment section component.
 * Only renders if there are tasks - no empty placeholders.
 */
interface SegmentSectionProps {
  label: string;
  tasks: ScheduledStep[];
  participants: Participant[];
  onNavigateToTask: (taskId: string) => void;
}

const SegmentSection: React.FC<SegmentSectionProps> = ({
  label,
  tasks,
  participants,
  onNavigateToTask,
}) => {
  // Don't render if no tasks
  if (tasks.length === 0) return null;

  // Determine if we should use 2-column layout (for small tasks, effort <= 0.25)
  const useGrid = tasks.length >= 2 && tasks.every((t) => t.effort <= 0.25);

  return (
    <Box sx={{ mb: 2.5 }}>
      {/* Segment header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1,
          px: 0.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            color: colors.gray[500],
            textTransform: 'uppercase',
            fontSize: '0.65rem',
            letterSpacing: 0.5,
          }}
        >
          {label}
        </Typography>
        <Typography variant="caption" sx={{ color: colors.gray[400], fontSize: '0.6rem' }}>
          {tasks.length}
        </Typography>
      </Box>

      {/* Task cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: useGrid ? 'repeat(2, 1fr)' : '1fr',
          gap: 1,
        }}
      >
        {tasks.map((scheduledStep) => (
          <TaskCard
            key={scheduledStep.step.id}
            scheduledStep={scheduledStep}
            variant="full"
            assignee={getAssignee(scheduledStep, participants)}
            isWaiting={scheduledStep.isWaiting}
            onClick={() => onNavigateToTask(scheduledStep.step.id)}
          />
        ))}
      </Box>
    </Box>
  );
};

/**
 * Main DayView component.
 */
export const DayView: React.FC<DayViewProps> = ({
  scheduleResult,
  currentDate,
  onDateChange,
  participants,
  currentParticipantId,
  onNavigateToTask,
}) => {
  // Get tasks for the current date
  const daySchedule = useMemo(
    () => getTasksForDate(scheduleResult, currentDate),
    [scheduleResult, currentDate]
  );

  // Navigate to previous/next day
  const handlePrevDay = () => {
    onDateChange(addDays(currentDate, -1));
  };

  const handleNextDay = () => {
    onDateChange(addDays(currentDate, 1));
  };

  // Get all tasks grouped by segment
  const allDayTasks = daySchedule?.allDayTasks || [];
  const morningTasks = daySchedule?.morningTasks || [];
  const afternoonTasks = daySchedule?.afternoonTasks || [];
  const eveningTasks = daySchedule?.eveningTasks || [];
  const waitingTasks = daySchedule?.waitingTasks || [];

  // Check if there are any tasks at all
  const hasMyTasks =
    allDayTasks.length > 0 ||
    morningTasks.length > 0 ||
    afternoonTasks.length > 0 ||
    eveningTasks.length > 0;

  return (
    <Box>
      {/* Header with date navigation */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          mb: 3,
          py: 1,
        }}
      >
        <IconButton
          onClick={handlePrevDay}
          size="small"
          sx={{ color: colors.gray[500] }}
        >
          <ChevronLeftIcon />
        </IconButton>

        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: colors.textPrimary,
            fontSize: '1rem',
            minWidth: 160,
            textAlign: 'center',
          }}
        >
          {formatDayHeader(currentDate)}
        </Typography>

        <IconButton
          onClick={handleNextDay}
          size="small"
          sx={{ color: colors.gray[500] }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {/* All-day tasks (if any) */}
      {allDayTasks.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1,
              px: 0.5,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: colors.blue[600],
                textTransform: 'uppercase',
                fontSize: '0.65rem',
                letterSpacing: 0.5,
              }}
            >
              All Day
            </Typography>
            <Typography variant="caption" sx={{ color: colors.gray[400], fontSize: '0.6rem' }}>
              {allDayTasks.length}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {allDayTasks.map((scheduledStep) => (
              <TaskCard
                key={scheduledStep.step.id}
                scheduledStep={scheduledStep}
                variant="full"
                assignee={getAssignee(scheduledStep, participants)}
                isWaiting={scheduledStep.isWaiting}
                onClick={() => onNavigateToTask(scheduledStep.step.id)}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Time segments - only show if they have tasks */}
      <SegmentSection
        label="Morning"
        tasks={morningTasks}
        participants={participants}
        onNavigateToTask={onNavigateToTask}
      />

      <SegmentSection
        label="Afternoon"
        tasks={afternoonTasks}
        participants={participants}
        onNavigateToTask={onNavigateToTask}
      />

      <SegmentSection
        label="Evening"
        tasks={eveningTasks}
        participants={participants}
        onNavigateToTask={onNavigateToTask}
      />

      {/* Empty state for my tasks */}
      {!hasMyTasks && waitingTasks.length === 0 && (
        <Box
          sx={{
            py: 4,
            textAlign: 'center',
            color: colors.gray[400],
          }}
        >
          <Typography variant="body2">No tasks scheduled for this day</Typography>
        </Box>
      )}

      {/* Waiting tasks section - separate from capacity */}
      {waitingTasks.length > 0 && (
        <Box
          sx={{
            mt: hasMyTasks ? 4 : 0,
            pt: hasMyTasks ? 3 : 0,
            borderTop: hasMyTasks ? `1px dashed ${colors.gray[200]}` : 'none',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1.5,
              px: 0.5,
            }}
          >
            <HourglassEmptyIcon sx={{ fontSize: 14, color: colors.amber[500] }} />
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: colors.amber[600],
                textTransform: 'uppercase',
                fontSize: '0.65rem',
                letterSpacing: 0.5,
              }}
            >
              Waiting on Others
            </Typography>
            <Typography variant="caption" sx={{ color: colors.gray[400], fontSize: '0.6rem' }}>
              {waitingTasks.length}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, opacity: 0.75 }}>
            {waitingTasks.map((scheduledStep) => (
              <TaskCard
                key={scheduledStep.step.id}
                scheduledStep={scheduledStep}
                variant="full"
                assignee={getAssignee(scheduledStep, participants)}
                isWaiting={true}
                onClick={() => onNavigateToTask(scheduledStep.step.id)}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Summary footer - shows capacity usage */}
      {daySchedule && daySchedule.totalEffort > 0 && (
        <Box
          sx={{
            mt: 3,
            pt: 2,
            borderTop: `1px solid ${colors.gray[100]}`,
            textAlign: 'center',
          }}
        >
          <Typography variant="caption" sx={{ color: colors.gray[400] }}>
            {Math.round(daySchedule.totalEffort * 100)}% capacity used
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default DayView;
